import { useEffect, useState } from 'react';
import { existe, t } from '../../i18n/idioma';
import type { CorpoPendenteView, ResgateView } from '../../world/useWorld';
import { relogio } from '../dueloTempo';
import { GumpWindow } from '../GumpWindow';
import { ItemIcon } from '../ItemIcon';
import { nomeDoItem } from '../../i18n/nomes';

/** O nome do lugar do corpo: o nome da caçada daquele mapa, se houver; senão o próprio id. */
export function nomeDoLugarDoCorpo(mapa: string): string {
  const chave = `cacada.${mapa}.nome`;
  return existe(chave) ? t(chave) : mapa;
}

/** O motivo curto da pílula vermelha ("corpo sumiu"); o longo vai no diário. */
export function motivoCurto(motivo: string): string {
  const chave = `resgate.curto.${motivo}`;
  return existe(chave) ? t(chave) : t('resgate.curto.falhou');
}

const CAVEIRA = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2C7 2 3.5 5.6 3.5 10.2c0 2.7 1.3 4.6 3 5.8V19a1 1 0 0 0 1 1h1.5v-2h2v2h2v-2h2v2h1.5a1 1 0 0 0 1-1v-3c1.7-1.2 3-3.1 3-5.8C20.5 5.6 17 2 12 2zm-3.5 11a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm7 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
  </svg>
);
const PLAY = <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5v9l7.5-4.5z" /></svg>;
const FECHAR = (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
  </svg>
);
/** O prazo inteiro do corpo some na barra a partir deste tanto (o `saque.jogador` padrão é 5 min). */
const PRAZO_BARRA_MS = 5 * 60_000;
/** Abaixo disto o relógio fica vermelho. */
const URGENTE_MS = 60_000;
/** A pílula verde do "recuperou" some sozinha (menos quando oferece continuar a caçada). */
const OK_SOME_MS = 30_000;

interface Props {
  corpo: CorpoPendenteView | null;
  resgate: ResgateView;
  /** Morreu caçando: a linha do lugar diz isso. */
  morreuCacando: boolean;
  /** "Continuar caçada" quando o resgate termina no mapa da caçada em que ele morreu. */
  podeContinuar: boolean;
  /** Outra pílula idle (caçada, coleta) já está no topo: esta desce uma linha. */
  abaixo: boolean;
  onResgatar(): void;
  onCancelar(): void;
  onContinuar(): void;
  onFecharCorpo(): void;
  onFecharResgate(): void;
}

/**
 * Resgate do corpo no padrão do HUD V3 (docs/prototipos/resgate-corpo-prototipo.html, Renato 03/11/2026): a pílula
 * idle no topo em cada etapa (corpo no chão, indo, recuperou, não deu) e a janela "Corpo", que só abre pelo clique na
 * pílula, com o lugar, o prazo, os itens nos encaixes da mochila e o botão. Nada fica no meio da tela.
 */
export function ResgateCorpo({
  corpo, resgate, morreuCacando, podeContinuar, abaixo, onResgatar, onCancelar, onContinuar, onFecharCorpo, onFecharResgate
}: Props) {
  const [agora, setAgora] = useState(() => Date.now());
  const [janela, setJanela] = useState(false);
  const [confirma, setConfirma] = useState(false);
  const contando = !!corpo && resgate.etapa === 0;
  useEffect(() => {
    if (!contando) return;
    const id = window.setInterval(() => setAgora(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [contando]);
  useEffect(() => {
    if (!confirma) return;
    const id = window.setTimeout(() => setConfirma(false), 3000);
    return () => window.clearTimeout(id);
  }, [confirma]);
  useEffect(() => {
    if (resgate.etapa !== 2 || podeContinuar) return;
    const id = window.setTimeout(onFecharResgate, OK_SOME_MS);
    return () => window.clearTimeout(id);
  }, [resgate.etapa, podeContinuar, onFecharResgate]);
  // A janela é do corpo no chão: começou o resgate (ou o corpo sumiu), fecha.
  useEffect(() => { if (!contando) setJanela(false); }, [contando]);

  const classe = `cl-min rg-min${abaixo ? ' rg-min--abaixo' : ''}`;

  if (resgate.etapa === 1) {
    return (
      <div className={classe}>
        <div className="idle-pill rg-pill" role="status" aria-label={t('resgate.pilula.indo')}>
          <span className="idle-pill__abrir rg-pill__so">
            <span className="idle-pill__arte">{CAVEIRA}</span>
            <span className="ch-pulso is-viagem"><i />{t('resgate.pilula.indo')}</span>
          </span>
          <button type="button" className="idle-pill__b idle-pill__b--sair" title={t('resgate.cancelar')} aria-label={t('resgate.cancelar')} onClick={onCancelar}>
            {FECHAR}
          </button>
        </div>
      </div>
    );
  }

  if (resgate.etapa === 2) {
    return (
      <div className={classe}>
        <div className="idle-pill rg-pill" role="status">
          <span className="idle-pill__abrir rg-pill__so">
            <span className="idle-pill__arte">{CAVEIRA}</span>
            <span className="ch-pulso is-ok"><i />{t('resgate.pilula.ok')}</span>
          </span>
          {podeContinuar && (
            <button type="button" className="idle-pill__b rg-pill__txt" onClick={onContinuar}>{PLAY}{t('resgate.continuar')}</button>
          )}
          <button type="button" className="idle-pill__b idle-pill__b--sair" title={t('comum.fechar')} aria-label={t('comum.fechar')} onClick={onFecharResgate}>
            {FECHAR}
          </button>
        </div>
      </div>
    );
  }

  if (resgate.etapa === 3) {
    return (
      <div className={classe}>
        <div className="idle-pill rg-pill" role="alert">
          <span className="idle-pill__abrir rg-pill__so">
            <span className="idle-pill__arte">{CAVEIRA}</span>
            <span className="ch-pulso is-fim"><i />{t('resgate.pilula.parou', { motivo: motivoCurto(resgate.motivo) })}</span>
          </span>
          <button type="button" className="idle-pill__b idle-pill__b--sair" title={t('comum.fechar')} aria-label={t('comum.fechar')} onClick={onFecharResgate}>
            {FECHAR}
          </button>
        </div>
      </div>
    );
  }

  if (!corpo) return null;
  const falta = corpo.ate - agora;
  if (falta <= 0) return null;
  const lugar = nomeDoLugarDoCorpo(corpo.mapa);
  const urgente = falta < URGENTE_MS;
  const recuperar = () => { setJanela(false); onResgatar(); };

  return (
    <>
      <div className={classe}>
        <div className="idle-pill rg-pill" role="group" aria-label={t('resgate.pilula.corpo', { lugar })}>
          <button type="button" className="idle-pill__abrir" onClick={() => setJanela(v => !v)} title={t('resgate.pilula.abrir')}>
            <span className="idle-pill__arte">{CAVEIRA}</span>
            <span className="ch-pulso is-corpo"><i />{t('resgate.pilula.corpo', { lugar })}</span>
            <b className={urgente ? 'rg-urgente' : undefined}>{relogio(falta)}</b>
          </button>
          <button type="button" className="idle-pill__b" title={t('resgate.botao')} aria-label={t('resgate.botao')} onClick={recuperar}>{PLAY}</button>
          <button
            type="button"
            className={`idle-pill__b idle-pill__b--sair${confirma ? ' is-confirma' : ''}`}
            title={confirma ? t('resgate.dispensarConfirma') : t('resgate.agoraNao')}
            aria-label={confirma ? t('resgate.dispensarConfirma') : t('resgate.agoraNao')}
            onClick={() => {
              if (!confirma) { setConfirma(true); return; }
              setConfirma(false);
              setJanela(false);
              onFecharCorpo();
            }}
          >
            {confirma ? <span>{t('resgate.dispensar')}</span> : FECHAR}
          </button>
        </div>
      </div>

      {janela && (
        <GumpWindow title={t('resgate.janela')} anchor={{ left: 0, top: 104 }} center className="win--corpo" posicao="corpo" onClose={() => setJanela(false)}>
          <div className="rg-lugar">
            <span className="rg-lugar__ic">{CAVEIRA}</span>
            <span className="rg-lugar__n">
              <b>{lugar}</b>
              <small>{t(morreuCacando ? 'resgate.lugar.cacando' : 'resgate.lugar.morreu', { n: corpo.itens })}</small>
            </span>
            <span className={`rg-tempo${urgente ? ' rg-urgente' : ''}`}>{relogio(falta)}<small>{t('resgate.someEmCurto')}</small></span>
          </div>
          <div className="rg-barra" aria-hidden="true"><i style={{ width: `${Math.min(100, (falta / PRAZO_BARRA_MS) * 100)}%` }} /></div>
          <div className="rg-rot"><span>{t('resgate.noCorpo')}</span><span>{t('resgate.nItens', { n: corpo.itens })}</span></div>
          <div className="rg-grade">
            {corpo.lista.map((it, i) => (
              <div key={`${it.defId}-${i}`} className={`rg-sock${it.vestido ? ' is-vestido' : ''}`} title={nomeDoItem(it.defId)}>
                <ItemIcon id={it.defId} size={30} quantity={it.qty > 1 ? it.qty : undefined} label={nomeDoItem(it.defId)} />
              </div>
            ))}
          </div>
          {corpo.lista.some(it => it.vestido) && <p className="rg-leg"><i />{t('resgate.vestidoLegenda')}</p>}
          <ol className="rg-como">
            <li><b>1</b>{t('resgate.passo1')}</li>
            <li><b>2</b>{t('resgate.passo2', { lugar })}</li>
            <li><b>3</b>{t('resgate.passo3')}</li>
          </ol>
          <div className="rg-acoes">
            <button type="button" className="btn btn--primary" onClick={recuperar}>{t('resgate.botao')}</button>
            <button type="button" className="btn btn--ghost" onClick={() => setJanela(false)}>{t('resgate.agoraNao')}</button>
          </div>
          <p className="rg-pe">{t('resgate.revela')}</p>
        </GumpWindow>
      )}
    </>
  );
}
